import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Trophy, Sparkle, ArrowRight } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import api from '@/utils/api';

const OnboardingChecklist = ({ onAction }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    fetchChecklist();
  }, []);

  const fetchChecklist = async () => {
    try {
      const response = await api.get('/onboarding/checklist');
      setChecklist(response.data);
      // Auto-collapse if all items completed
      if (response.data.progress_percentage === 100) {
        setCollapsed(true);
      }
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActionForItem = (itemId) => {
    // SYSTEM ORDER: Profile → Offer → Sales Page → Lead Magnet → Emails → Viral Ideas → Content → 3 Posts
    const actionMap = {
      'complete_profile': 'profile',
      'build_first_offer': 'build-offer',
      'create_sales_page': 'sales-page',
      'create_lead_magnet': 'lead-magnet',
      'create_emails': 'email-sequence',
      'generate_viral_ideas': 'viral-ideas',
      'create_first_content': 'generate-content',
      'create_3_posts': 'generate-content',
    };
    return actionMap[itemId] || null;
  };

  const handleItemClick = (item) => {
    if (item.completed) return;
    
    const action = getActionForItem(item.id);
    if (action && onAction) {
      onAction(action);
    }
  };

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-5 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-white/10 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!checklist) return null;

  // If all complete and collapsed, show compact version
  if (collapsed && checklist.progress_percentage === 100) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4 cursor-pointer"
        onClick={() => setCollapsed(false)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
              <Trophy size={20} className="text-white" weight="fill" />
            </div>
            <div>
              <h4 className="text-white font-semibold">You're All Set Up!</h4>
              <p className="text-emerald-400 text-sm">Great job completing everything</p>
            </div>
          </div>
          <Sparkle size={24} className="text-yellow-400" weight="fill" />
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-5"
      data-testid="onboarding-checklist"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold">Let's Get You Started</h3>
          <p className="text-sm text-slate-400">
            {checklist.completed_count} of {checklist.total_count} done
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${checklist.progress_percentage}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
            />
          </div>
          <span className="text-sm text-white font-bold">{checklist.progress_percentage}%</span>
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-2">
        {checklist.items.map((item, index) => (
          <motion.div
            key={item.id}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={() => handleItemClick(item)}
            className={`flex items-center justify-between p-3 rounded-lg transition-all ${
              item.completed
                ? 'bg-emerald-500/10 border border-emerald-500/20'
                : 'bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 cursor-pointer'
            }`}
            data-testid={`checklist-item-${item.id}`}
          >
            <div className="flex items-center space-x-3">
              {item.completed ? (
                <CheckCircle size={20} className="text-emerald-400" weight="fill" />
              ) : (
                <Circle size={20} className="text-slate-500" />
              )}
              <div>
                <p className={`text-sm font-medium ${item.completed ? 'text-emerald-400' : 'text-white'}`}>
                  {item.title}
                </p>
                <p className="text-xs text-slate-500">{item.description}</p>
              </div>
            </div>
            {!item.completed && (
              <ArrowRight size={16} className="text-slate-400" />
            )}
          </motion.div>
        ))}
      </div>

      {/* Collapse button when all complete */}
      {checklist.progress_percentage === 100 && (
        <button
          onClick={() => setCollapsed(true)}
          className="mt-4 w-full py-2 text-sm text-slate-400 hover:text-white transition-colors"
        >
          Minimize
        </button>
      )}
    </motion.div>
  );
};

export default OnboardingChecklist;
